import React, {
  FC,
  useEffect,
  useState,
  useImperativeHandle,
  useRef,
  forwardRef as forwardRefReact,
  useLayoutEffect
} from 'react';
import Typography from '@mui/material/Typography';
import { SliceStop, updateStopV2 } from '../../redux/features/orders/ordersSlice';
import Box from '@mui/material/Box';
import { StopOrderV2 } from '../TwStop/TwStop';
import DateUtils from '../../utils/dateUtils';
import Grid from '@mui/material/Grid';
import TwAutocomplete, { TwAutocompleteOption } from '../TwAutocomplete/TwAutocomplete';
import { STOP_TYPE, STOP_TYPES } from '../../redux/api/trips/trips.model';
import TwField, { TwInputTypes } from '../TwFields/TwFields';
import StopNoteContactInfo from '../TwStop/components/StopNoteContactInfo';
import { Characters, useUnformatValue } from '../../hooks/useAllowCharacters';
import TwDatePicker from '../TwDatePicker/TwDatePicker';
import { useGetCreateLoadConfigQuery } from '../../redux/api/load/loadApi';
import { CreateLoadFields, LoadLocation } from '../../redux/api/load/load.model';
import LoadUtils from '../../utils/loadUtils';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { FormikValues, useFormikContext } from 'formik';
import useAuth from '../../providers/Auth/Auth.provider';
import TwTimePicker from '../TwTimePicker/TwTimePicker';
import { TZ_USAGE } from '../../redux/api/orders/orders.model';
import { parse } from 'date-fns';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material/styles';
import CheckedDateTime from '../TwStop/components/CheckedDateTime';
import InfoIcon from '@mui/icons-material/Info';
import { forwardRef } from 'react';
import { TwStopHandle } from '../TwStop/TwStop';
import { useStopScroll } from '../../hooks/useStopScroll';
import { setAnchorId } from '../../redux/features/navigation/navigationSlice';

interface TwSharedStopProps {
  stop: SliceStop;
  hasLocationBeenUpdated: boolean;
  isImport: boolean;
  isCreateOrder?: boolean;
}

const TwOnlyinfoStop = forwardRef<TwStopHandle, TwSharedStopProps>(
  ({ stop, hasLocationBeenUpdated, isImport, isCreateOrder }, ref) => {
    const {
      getTenantSetting,
      data: { userData }
    } = useAuth();
    const dispatch = useAppDispatch();
    const { status, timezone_usage, order_id } = useAppSelector((state) => state.orders);
    const { data: createLoadConfig } = useGetCreateLoadConfigQuery();
    const { errors } = useFormikContext<FormikValues>();
    const unformatString = useUnformatValue(Characters.number);
    const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
    const enableAppointmentWindow = getTenantSetting('enable-appointment-window', false);
    const use24HourClock = getTenantSetting('use-24-hour-clock', false);
    const isEnabledShowCheckInOutTimes = getTenantSetting('show-check-in-and-check-out-times');

    const [requiredFields, setRequiredFields] = useState<CreateLoadFields[] | never>([]);
    const [date, setDate] = useState<Date | null | undefined>(null);
    const [timeFrom, setTimeFrom] = useState<Date | null | undefined>(null);
    const [timeTo, setTimeTo] = useState<Date | null | undefined>(null);
    const [stopIdentifier, setStopIdentifier] = useState<string | undefined>(
      stop.provider_stop_identifier
    );

    // Add new state variables for check in/out times
    const [checkInDate, setCheckInDate] = useState<Date | null | undefined>(null);
    const [initialCheckInDate, setInitialCheckInDate] = useState<Date | null | undefined>(null);
    const [isCheckInEditing, setIsCheckInEditing] = useState<boolean>(false);

    const [checkInTime, setCheckInTime] = useState<Date | null | undefined>(null);
    const [initialCheckInTime, setInitialCheckInTime] = useState<Date | null | undefined>(null);

    const [checkOutDate, setCheckOutDate] = useState<Date | null | undefined>(null);
    const [initialCheckOutDate, setInitialCheckOutDate] = useState<Date | null | undefined>(null);
    const [isCheckOutEditing, setIsCheckOutEditing] = useState<boolean>(false);

    const [checkOutTime, setCheckOutTime] = useState<Date | null | undefined>(null);
    const [initialCheckOutTime, setInitialCheckOutTime] = useState<Date | null | undefined>(null);

    const stopOrderIndicatorRef = useRef<HTMLSpanElement>(null);

    const { stopRef, inTimeRef, outTimeRef } = useStopScroll({
      stop,
      setIsCheckInEditing,
      setIsCheckOutEditing
    });

    const entity_id = order_id;

    useImperativeHandle(ref, () => ({
      get stopOrderIndicator(): HTMLSpanElement | null {
        return stopOrderIndicatorRef.current;
      }
    }));

    const handleLocationNotesChange = (value: string, field: string): void => {
      const newStop = { ...stop, [field]: value };
      dispatch(updateStopV2({ stop: newStop }));
    };

    const handleLocationContactChange = (value: string, field: string): void => {
      let newStop = { ...stop };
      if (field === 'contact_number') {
        newStop = { ...stop, contact: { ...stop.contact, contact_number: unformatString(value) } };
      } else {
        newStop = { ...stop, contact: { ...stop.contact, [field]: value } };
      }

      dispatch(updateStopV2({ stop: newStop }));
    };

    useEffect(() => {
      const required = LoadUtils.getRequiredFields(status, createLoadConfig || []);
      setRequiredFields(required);
    }, [status]);

    // #region Effects to update to the redux store
    useEffect(() => {
      const newStop = { ...stop };
      newStop.provider_date = date;
      dispatch(updateStopV2({ stop: newStop }));
    }, [date]);

    useEffect(() => {
      const newStop = { ...stop };
      newStop.provider_time_from = timeFrom;

      dispatch(updateStopV2({ stop: newStop }));
    }, [timeFrom]);

    useEffect(() => {
      const newStop = { ...stop };
      newStop.provider_time_to = timeTo;

      dispatch(updateStopV2({ stop: newStop }));
    }, [timeTo]);

    // #endregion of effects to redux store

    // Add new useEffect blocks for check in/out times
    useEffect(() => {
      setInitialCheckInDate(stop.provider_check_in_date || null);
      setCheckInDate(stop.provider_check_in_date || null);
    }, [stop.provider_check_in_date]);

    useEffect(() => {
      const newStop = { ...stop };

      if (
        checkInDate &&
        (!initialCheckInDate ||
          new Date(checkInDate).setSeconds(0, 0) !== new Date(initialCheckInDate).setSeconds(0, 0))
      ) {
        newStop.provider_check_in_date = checkInDate;
        newStop.provider_stop_in_time_overriden = true;
      }

      dispatch(updateStopV2({ stop: newStop }));
    }, [checkInDate, initialCheckInDate]);

    useEffect(() => {
      setInitialCheckInTime(stop.provider_check_in_time || null);
      setCheckInTime(stop.provider_check_in_time || null);
    }, [stop.provider_check_in_time]);

    useEffect(() => {
      const newStop = { ...stop };

      if (
        checkInTime &&
        (!initialCheckInTime ||
          new Date(checkInTime).setSeconds(0, 0) !== new Date(initialCheckInTime).setSeconds(0, 0))
      ) {
        newStop.provider_check_in_time = checkInTime;
        newStop.provider_stop_in_time_overriden = true;
      }

      dispatch(updateStopV2({ stop: newStop }));
    }, [checkInTime, initialCheckInTime]);

    useEffect(() => {
      setInitialCheckOutDate(stop.provider_check_out_date || null);
      setCheckOutDate(stop.provider_check_out_date || null);
    }, [stop.provider_check_out_date]);

    useEffect(() => {
      const newStop = { ...stop };

      if (
        checkOutDate &&
        (!initialCheckOutDate ||
          new Date(checkOutDate).setSeconds(0, 0) !==
            new Date(initialCheckOutDate).setSeconds(0, 0))
      ) {
        newStop.provider_check_out_date = checkOutDate;
        newStop.provider_stop_out_time_overriden = true;
      }

      dispatch(updateStopV2({ stop: newStop }));
    }, [checkOutDate, initialCheckOutDate]);

    useEffect(() => {
      setInitialCheckOutTime(stop.provider_check_out_time || null);
      setCheckOutTime(stop.provider_check_out_time || null);
    }, [stop.provider_check_out_time]);

    useEffect(() => {
      const newStop = { ...stop };

      if (
        checkOutTime &&
        (!initialCheckOutTime ||
          new Date(checkOutTime).setSeconds(0, 0) !==
            new Date(initialCheckOutTime).setSeconds(0, 0))
      ) {
        newStop.provider_check_out_time = checkOutTime;
        newStop.provider_stop_out_time_overriden = true;
      }

      dispatch(updateStopV2({ stop: newStop }));
    }, [checkOutTime, initialCheckOutTime]);

    /**
     * Why do we update Redux immediately in these handlers instead of using useEffect?
     *
     * - If we used useEffect to watch for changes in the local state (e.g., checkInDate),
     *   there would be a race condition: when a user edits a field and immediately saves,
     *   the Redux state might not be updated in time for the save action, causing the API
     *   request to send stale/original values instead of the user's changes.
     *
     * - By dispatching the Redux update directly in the onChange handler, we ensure that
     *   Redux always has the latest value as soon as the user makes a change. This guarantees
     *   that when the user clicks Save, the API request will include the most up-to-date data.
     *
     * - This pattern is especially important for fields that are edited and saved quickly,
     *   such as date/time pickers, where the user may not trigger a blur or other event before saving.
     */
    const handleCheckInDateChange = (date: Date | null): void => {
      setCheckInDate(date);
      const newStop = {
        ...stop,
        provider_check_in_date: date,
        provider_stop_in_time_overriden: true
      };
      dispatch(updateStopV2({ stop: newStop }));
    };

    const handleCheckInTimeChange = (time: Date | null): void => {
      setCheckInTime(time);
      const newStop = {
        ...stop,
        provider_check_in_time: time,
        provider_stop_in_time_overriden: true
      };
      dispatch(updateStopV2({ stop: newStop }));
    };

    const handleCheckOutDateChange = (date: Date | null): void => {
      setCheckOutDate(date);
      const newStop = {
        ...stop,
        provider_check_out_date: date,
        provider_stop_out_time_overriden: true
      };
      dispatch(updateStopV2({ stop: newStop }));
    };

    const handleCheckOutTimeChange = (time: Date | null): void => {
      setCheckOutTime(time);
      const newStop = {
        ...stop,
        provider_check_out_time: time,
        provider_stop_out_time_overriden: true
      };
      dispatch(updateStopV2({ stop: newStop }));
    };

    return (
      <Box className="stop__onlyinfo-container" key={stop.ui_id} ref={stopRef}>
        <Box display={'flex'}>
          <StopOrderV2 islast={'false'} ref={stopOrderIndicatorRef}>
            {stop.order}
          </StopOrderV2>
          <Typography
            component="span"
            sx={{ fontWeight: '600', color: '#1F2937', fontSize: '14px' }}
          >
            Stop
          </Typography>
        </Box>

        <Grid container gap={1}>
          <Grid item xs={7.5}>
            <TwField
              name={`shared-stop-location-${stop.order}`}
              label="Stop location"
              inputType={TwInputTypes.text}
              value={stop.provider_location?.label}
              disabled
              sx={{
                '.MuiInputBase-root.Mui-disabled, .MuiInputBase-root.Mui-disabled  input.MuiInputBase-input.Mui-disabled, .MuiInputBase-root.Mui-disabled button.MuiButtonBase-root.Mui-disabled':
                  {
                    backgroundColor: '#F9FAFB'
                  }
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <TwAutocomplete
              name={`shared-stop-type-${stop.order}`}
              label="Type"
              optionList={STOP_TYPES}
              value={stop.provider_stop_type}
              onChange={(_, newValue: TwAutocompleteOption | null): void => {
                if (!newValue) return;
                const newStop = { ...stop };
                newStop.stop_type = newValue.id as STOP_TYPE;
                newStop.provider_stop_type = newValue;
                dispatch(updateStopV2({ stop: newStop }));
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <TwField
              id={`shared-stop-pickupnumber-${stop.order}`}
              name={`shared-stop-pickupnumber-${stop.order}`}
              label={
                stop.stop_type === STOP_TYPE.PICKUP ||
                stop.provider_stop_type?.value === STOP_TYPE.PICKUP
                  ? 'Pick-up #'
                  : stop.stop_type === STOP_TYPE.DROP_OFF ||
                    stop.provider_stop_type?.value === STOP_TYPE.DROP_OFF
                  ? 'Delivery #'
                  : 'Transfer #'
              }
              inputType={TwInputTypes.text}
              value={stopIdentifier}
              onInputChange={(e): void => {
                const newStop = { ...stop };
                newStop.provider_stop_identifier = e.target.value;
                newStop.stop_identifier = e.target.value;
                setStopIdentifier(e.target.value);
              }}
              onInputBlur={(e): void => {
                const newStop = { ...stop };
                newStop.provider_stop_identifier = stopIdentifier;
                newStop.stop_identifier = stopIdentifier;

                dispatch(updateStopV2({ stop: newStop }));
              }}
            />
          </Grid>
        </Grid>
        <StopNoteContactInfo
          isNoteVisible={!!stop.provider_include_location_notes}
          hasLocationBeenUpdated={hasLocationBeenUpdated}
          toggleNoteVisibility={(): void => {
            const newStop = {
              ...stop,
              provider_include_location_notes: !stop.provider_include_location_notes
            };
            //  dispatch(updateStopV2({ stop: newStop }));
          }}
          initialNote={stop.notes || ''}
          initialEmail={stop.contact?.contact_email || ''}
          initialName={stop.contact?.contact_name || ''}
          initialPhone={stop.contact?.contact_number || ''}
          initialExt={stop.contact?.contact_number_ext || ''}
          handleFieldChange={handleLocationNotesChange}
          handleLocationContactChange={handleLocationContactChange}
          isImportLoad={isImport}
          disableCheckbox={false}
        />

        <Box
          flexDirection={isImport || matchesMobileOrTablet ? 'column' : 'row'}
          className={
            isImport || matchesMobileOrTablet ? 'stop__import-date-time' : 'stop__date-time'
          }
        >
          <TwDatePicker
            name={`stop-date-${stop.order}`}
            label={`Scheduled date ${
              requiredFields.includes('start_date') || requiredFields.includes('end_date')
                ? '*'
                : ''
            }`}
            containerClassName="stop__date"
            value={stop.provider_date}
            placeholder="MM/DD/YY"
            onChange={(newDate): void => {
              const date = newDate as Date | null;
              setDate(date);
            }}
            onInputBlur={(e): void => {
              try {
                const date = new Date(e.target.value);
                setDate(date.toISOString() ? date : null);
              } catch (e) {
                setDate(null);
              }
            }}
            error={!!errors[`stop-date-${stop.order}`]}
          />
          <Box
            className={!isCreateOrder ? 'stop__time-container' : ''}
            display={isCreateOrder ? 'flex' : 'initial'}
          >
            <>
              <TwTimePicker
                name={`stop-time-from-${stop.order}`}
                label={`${enableAppointmentWindow ? 'From' : 'Scheduled time'} (${
                  timezone_usage === TZ_USAGE.LOCAL && stop.provider_location
                    ? (stop.provider_location?.value as LoadLocation)?.short_offset
                    : userData?.short_offset
                })`}
                value={stop.provider_time_from}
                containerClassName={isImport ? 'stop__import-time-hide' : 'stop__time-hide'}
                onChange={(value): void => {
                  const validInput = value !== 'hh:mm aa' && value !== 'hh:mm';
                  const date = value as Date | null;
                  validInput ? setTimeFrom(date) : setTimeFrom(null);
                }}
                onInputBlur={(e): void => {
                  const validInput = e.target.value !== 'hh:mm aa' && e.target.value !== 'hh:mm';
                  try {
                    const date = parse(
                      e.target.value as string,
                      !use24HourClock ? 'hh:mm aa' : 'HH:mm',
                      new Date()
                    );

                    validInput ? setTimeFrom(date) : setTimeFrom(null);
                  } catch (e) {
                    setTimeFrom(null);
                  }
                }}
                ampm={!use24HourClock}
                placeholder={!use24HourClock ? 'HH:MM AM' : 'HH:MM'}
              />

              {enableAppointmentWindow && (
                // Tabindex here helps focus on the second time field which was often missed in keyboard navigation
                <div tabIndex={0}>
                  <TwTimePicker
                    name={`stop-time-to-${stop.order}`}
                    label={`To (${
                      timezone_usage === TZ_USAGE.LOCAL && stop.provider_location
                        ? (stop.provider_location?.value as LoadLocation)?.short_offset
                        : userData?.short_offset
                    })`}
                    value={stop.provider_time_to}
                    containerClassName={isImport ? 'stop__import-time-hide' : 'stop__time-hide'}
                    onChange={(value): void => {
                      const validInput = value !== 'hh:mm aa' && value !== 'hh:mm';
                      const date = value as Date | null;
                      validInput ? setTimeTo(date) : setTimeTo(null);
                    }}
                    onInputBlur={(e): void => {
                      const validInput =
                        e.target.value !== 'hh:mm aa' && e.target.value !== 'hh:mm';
                      try {
                        const date = parse(
                          e.target.value as string,
                          !use24HourClock ? 'hh:mm aa' : 'HH:mm',
                          new Date()
                        );
                        validInput ? setTimeTo(date) : setTimeTo(null);
                      } catch (e) {
                        setTimeTo(null);
                      }
                    }}
                    ampm={!use24HourClock}
                    placeholder={!use24HourClock ? 'HH:MM AM' : 'HH:MM'}
                  />
                </div>
              )}
            </>
          </Box>
        </Box>

        {/* Add Actual In/Out time sections */}
        {!isCheckInEditing && checkInDate && checkInTime && !isCreateOrder && (
          <Box display="flex" flexDirection="column">
            <CheckedDateTime
              date={checkInDate}
              time={checkInTime}
              label="Actual in time: "
              onEditClick={(): void => {
                setIsCheckInEditing(true);
              }}
              shortOffset={` (${DateUtils.getBrowserTimeZoneOffsetNameShort()})`}
              use24HourFormat={use24HourClock}
            />
          </Box>
        )}
        {isCheckInEditing && isEnabledShowCheckInOutTimes && !isCreateOrder && (
          <Box
            ref={inTimeRef}
            id={`stop-check-in-time-${stop.segment_id}-${stop.id}`}
            flexDirection={matchesMobileOrTablet ? 'column' : 'row'}
            className={matchesMobileOrTablet ? 'stop__import-date-time' : 'stop__date-time'}
          >
            <TwDatePicker
              name={`stop-check-in-date-${stop.order}`}
              label="Actual in date*"
              containerClassName="stop__date"
              value={checkInDate || initialCheckInDate}
              placeholder="MM/DD/YY"
              onChange={(newDate): void => {
                handleCheckInDateChange(newDate as Date | null);
              }}
              onInputBlur={(e): void => {
                try {
                  const date = new Date(e.target.value);
                  handleCheckInDateChange(date.toISOString() ? date : null);
                } catch (e) {
                  handleCheckInDateChange(null);
                }
              }}
              error={!!errors[`stop-check-in-date-${stop.order}`]}
            />
            <TwTimePicker
              name={`stop-check-in-time-${stop.order}`}
              label={`Actual in time (${DateUtils.getBrowserTimeZoneOffsetNameShort()})`}
              value={checkInTime || initialCheckInTime}
              containerClassName="stop__time-hide"
              onChange={(value): void => {
                const validInput = value !== 'hh:mm aa' && value !== 'hh:mm';
                handleCheckInTimeChange(validInput ? (value as Date | null) : null);
              }}
              onInputBlur={(e): void => {
                try {
                  const date = parse(
                    e.target.value as string,
                    !use24HourClock ? 'hh:mm aa' : 'HH:mm',
                    new Date()
                  );
                  handleCheckInTimeChange(date);
                } catch (e) {
                  handleCheckInTimeChange(null);
                }
              }}
              ampm={!use24HourClock}
              placeholder={!use24HourClock ? 'HH:MM AM' : 'HH:MM'}
              error={!!errors[`stop-check-in-time-${stop.order}`]}
            />
          </Box>
        )}
        {!isCheckOutEditing &&
          checkOutDate &&
          checkOutTime &&
          isEnabledShowCheckInOutTimes &&
          !isCreateOrder && (
            <Box display="flex" flexDirection="column">
              <CheckedDateTime
                date={checkOutDate}
                time={checkOutTime}
                label="Actual out time: "
                onEditClick={(): void => {
                  setIsCheckOutEditing(true);
                }}
                shortOffset={` (${DateUtils.getBrowserTimeZoneOffsetNameShort()})`}
                use24HourFormat={use24HourClock}
              />
            </Box>
          )}
        {isCheckOutEditing && isEnabledShowCheckInOutTimes && !isCreateOrder && (
          <Box
            ref={outTimeRef}
            id={`stop-check-out-time-${stop.segment_id}-${stop.id}`}
            flexDirection={matchesMobileOrTablet ? 'column' : 'row'}
            className={matchesMobileOrTablet ? 'stop__import-date-time' : 'stop__date-time'}
          >
            <TwDatePicker
              name={`stop-check-out-date-${stop.order}`}
              label="Actual out date*"
              containerClassName="stop__date"
              value={checkOutDate || initialCheckOutDate}
              placeholder="MM/DD/YY"
              onChange={(newDate): void => {
                handleCheckOutDateChange(newDate as Date | null);
              }}
              onInputBlur={(e): void => {
                try {
                  const date = new Date(e.target.value);
                  handleCheckOutDateChange(date.toISOString() ? date : null);
                } catch (e) {
                  handleCheckOutDateChange(null);
                }
              }}
              error={!!errors[`stop-check-out-date-${stop.order}`]}
            />
            <Box display={'flex'} flexDirection={'row'}>
              <TwTimePicker
                name={`stop-check-out-time-${stop.order}`}
                label={`Actual out time (${DateUtils.getBrowserTimeZoneOffsetNameShort()})`}
                value={checkOutTime || initialCheckOutTime}
                containerClassName="stop__time-hide"
                onChange={(value): void => {
                  const validInput = value !== 'hh:mm aa' && value !== 'hh:mm';
                  handleCheckOutTimeChange(validInput ? (value as Date | null) : null);
                }}
                onInputBlur={(e): void => {
                  try {
                    const date = parse(
                      e.target.value as string,
                      !use24HourClock ? 'hh:mm aa' : 'HH:mm',
                      new Date()
                    );
                    handleCheckOutTimeChange(date);
                  } catch (e) {
                    handleCheckOutTimeChange(null);
                  }
                }}
                ampm={!use24HourClock}
                placeholder={!use24HourClock ? 'HH:MM AM' : 'HH:MM'}
                error={!!errors[`stop-check-out-time-${stop.order}`]}
              />
            </Box>
          </Box>
        )}
      </Box>
    );
  }
);

TwOnlyinfoStop.displayName = 'TwOnlyinfoStop';
export default TwOnlyinfoStop;
